import { Link } from "react-router-dom";
import { PackageOpen, PackageCheck, ShoppingBag, Wallet, Rocket, TrendingUp, Percent, ChevronRight } from "lucide-react";
import { formatKrWhole } from "../lib/format";

const rateTone = (r) => (r == null ? "neutral" : r >= 80 ? "good" : r >= 60 ? "ok" : "low");
const RATE_LABEL = { good: "God", ok: "OK", low: "Lav" };

function Trend({ value, unit, testid }) {
  if (value == null) return <span className="kc-trend flat" data-testid={testid}>— vs. forrige 30 dage</span>;
  const up = value > 0;
  const cls = value === 0 ? "flat" : up ? "up" : "down";
  const arrow = value === 0 ? "■" : up ? "▲" : "▼";
  return (
    <span className={`kc-trend ${cls}`} data-testid={testid}>
      <span aria-hidden="true">{arrow}</span> {Math.abs(value)} {unit}
      <span className="kc-trend-vs"> vs. forrige 30 dage</span>
    </span>
  );
}

export function KpiCard({ to, onClick, icon: Icon, tone = "green", value, unit, label, sub, testid }) {
  const body = (
    <>
      <span className={`kc-icon t-${tone}`} aria-hidden="true">
        <Icon size={18} />
      </span>
      <span className="kc-value" data-testid={`${testid}-value`}>
        {value}
        {unit && <span className="kc-unit">{unit}</span>}
      </span>
      <span className="kc-label">{label}</span>
      {sub && <span className="kc-sub">{sub}</span>}
      {(to || onClick) && <ChevronRight className="kc-chev" size={16} aria-hidden="true" />}
    </>
  );
  if (!to && !onClick) return <div className="kcard static" data-testid={testid}>{body}</div>;
  return to ? (
    <Link className="kcard" to={to} data-testid={testid}>{body}</Link>
  ) : (
    <button type="button" className="kcard" onClick={onClick} data-testid={testid}>{body}</button>
  );
}

export function KpiSkeleton() {
  return (
    <section className="kstrip" aria-label="Nøgletal indlæses" aria-busy="true" data-testid="kpi-skeleton">
      {Array.from({ length: 7 }, (_, i) => (
        <div className="kcard skel" key={i}>
          <span className="sk sk-icon" />
          <span className="sk sk-num" />
          <span className="sk sk-line" />
        </div>
      ))}
    </section>
  );
}

export default function KpiStrip({ k, onOffers }) {
  const tone = rateTone(k.rate);
  const lw = k.picked_same_weekday_last_week;
  return (
    <section className="kstrip" aria-label="Nøgletal" data-testid="kpi-strip">
      <KpiCard to="/pickup" icon={PackageOpen} value={k.today_n} label={`skal hentes i dag (${k.today_q} stk.)`} testid="kpi-today" />
      <KpiCard
        to="/pickup?tab=udleveret"
        icon={PackageCheck}
        value={k.picked_today}
        label="udleveret i dag"
        sub={lw != null && <span className="kc-trend flat">{lw} samme dag sidste uge</span>}
        testid="kpi-picked-today"
      />
      <KpiCard to="/pickup" icon={ShoppingBag} tone="amber" value={k.new_orders_today ?? 0} label="nye bestillinger i dag" testid="kpi-new-orders" />
      <KpiCard to="/pickup" icon={Wallet} tone="amber" value={formatKrWhole(k.open_value)} unit="kr." label="i åbne reservationer" testid="kpi-open-value" />
      <KpiCard onClick={onOffers} icon={Rocket} value={k.active_offers ?? 0} label="aktive tilbud" testid="kpi-active-offers" />
      <KpiCard
        to="/stats"
        icon={TrendingUp}
        value={formatKrWhole(k.revenue30)} unit="kr."
        label="omsat, 30 dage"
        sub={<Trend value={k.revenue30_change_pct} unit="%" testid="kpi-revenue-trend" />}
        testid="kpi-revenue30"
      />
      <KpiCard
        to="/stats"
        icon={Percent}
        tone={tone === "neutral" ? "green" : `rate-${tone}`}
        value={
          <>
            {k.rate == null ? "–" : `${k.rate} %`}
            {k.rate != null && <span className={`kc-badge r-${tone}`} data-testid="kpi-rate-badge">{RATE_LABEL[tone]}</span>}
          </>
        }
        label="afhentet, 30 dage"
        sub={<Trend value={k.rate_change_pts} unit="pct.-point" testid="kpi-rate-trend" />}
        testid="kpi-rate30"
      />
    </section>
  );
}
